<template>
  <div class="bg bglight">
    <div
      v-if="!isBackgroundLoading && backgroundStyle"
      class="apartment-mobile-photo"
      :style="backgroundStyle"
      role="img"
      :aria-label="content.name"
    ></div>
    <div
      v-else-if="isBackgroundLoading"
      class="apartment-mobile-photo apartment-mobile-photo--loading"
      aria-hidden="true"
    >
      <p>Bild wird geladen...</p>
    </div>

    <div class="container">
      <div class="row">
        <div class="col-md-6 col-sm-12">
          <div class="padding darkcolor equalheight left">
            <h2 class="text-capitalize heading_space">{{ content.name }}</h2>
            <div class="bottom0 top15 justify apartment-description" v-html="descriptionHtml"></div>
            
            <!-- Apartment Details -->
            <div class="apartment-details" style="margin: 20px 0;">
              <p><strong>Max. Gäste:</strong> {{ content.max_guests }}</p>
              <p><strong>Etage:</strong> {{ content.floor }}</p>
            </div>
            
            <!-- Ausstattung - nur anzeigen wenn vorhanden -->
            <div class="equipment" v-if="content.furnishings && content.furnishings.length > 0">
              <h3 class="text-captalize darkcolor bottom10 top30">
                Ausstattung
              </h3>
              <table class="furnishing-table">
                <tr
                  :key="furnishing.id"
                  v-for="furnishing in content.furnishings"
                >
                  <td class="icon">
                    <i :class="'fa ' + furnishing.furnishings_id.icon"></i>
                  </td>
                  <td class="description">
                    <p>{{ furnishing.furnishings_id.label }}</p>
                  </td>
                </tr>
              </table>
            </div>
            <div class="h-line apartment-actions">
              <BookingActionButtons
                show-availability
                @availability="openAvailabilityModal"
              />
            </div>
          </div>
        </div>
        
      </div>
    </div>

    <PopUp
      :is_open="availabilityModalOpen"
      :title="`Verfügbarkeit – ${content.name}`"
      size="lg"
      auto_height
      @onClose="closeAvailabilityModal"
    >
      <template #content>
        <ApartmentSelector
          v-if="apartments.length > 1"
          class="apartment-modal__switcher"
          :apartments="apartments"
          :selectedApartment="selected_apartment"
          @update:selectedApartment="onApartmentChange"
        />
        <AvailabilityCalendar
          :apartment="content"
          :active="availabilityModalOpen"
        />
      </template>
      <template #footer>
        <button
          type="button"
          class="button light"
          @click="openBookingModalFromAvailability"
        >
          Zeitraum reservieren
        </button>
      </template>
    </PopUp>
    <div
      v-if="!isBackgroundLoading && backgroundStyle"
      :style="backgroundStyle"
      class="bghalf hidden-xs"
    ></div>
    <div
      v-if="isBackgroundLoading"
      class="bghalf hidden-xs"
      style="background-color: #f5f5f5; display: flex; align-items: center; justify-content: center;"
    >
      <p>Bild wird geladen...</p>
    </div>
  </div>
</template>

<script>
import { useBackgroundImageLoader } from "../../composables/index.js";
import { useBookingModal } from "../../composables/useBookingModal.js";
import { onMounted, ref, watch, computed } from "vue";
import { normalizeInvertedStrongParagraphs } from "../../lib/normalizeCmsHtml.js";
import AvailabilityCalendar from "../misc/AvailabilityCalendar.vue";
import ApartmentSelector from "../misc/ApartmentSelector.vue";
import BookingActionButtons from "../misc/BookingActionButtons.vue";
import PopUp from "../misc/PopUp.vue";

export default {
  components: {
    AvailabilityCalendar,
    ApartmentSelector,
    BookingActionButtons,
    PopUp
  },
  props: {
    content: Object,
    selected_apartment: String,
    apartments: {
      type: Array,
      default: () => []
    },
    backgound_image: Object,
  },
  emits: ['update:selectedApartment'],
  setup(props, { emit }) {
    const { backgroundStyle, isBackgroundLoading, loadBackgroundImage } = useBackgroundImageLoader();
    const { openBookingModal } = useBookingModal();
    const availabilityModalOpen = ref(false);

    const descriptionHtml = computed(() =>
      normalizeInvertedStrongParagraphs(props.content?.description || '')
    );

    const loadImage = async () => {
      let imageToLoad = null;
      
      if (props.backgound_image && props.backgound_image.image) {
        imageToLoad = props.backgound_image;
      } else if (props.content && props.content.main_image) {
        imageToLoad = {
          image: props.content.main_image,
          position: 'center center',
          right: true
        };
      }

      if (imageToLoad) {
        await loadBackgroundImage(imageToLoad);
      }
    };

    const openAvailabilityModal = () => {
      availabilityModalOpen.value = true;
    };

    const closeAvailabilityModal = () => {
      availabilityModalOpen.value = false;
    };

    const openBookingModalFromAvailability = () => {
      closeAvailabilityModal();
      openBookingModal();
    };

    const onApartmentChange = (slug) => {
      if (slug === props.selected_apartment) return
      emit('update:selectedApartment', slug)
    };

    onMounted(() => {
      loadImage();
    });

    watch(() => [props.backgound_image, props.content], () => {
      loadImage();
      if (availabilityModalOpen.value) {
        availabilityModalOpen.value = false;
      }
    }, { deep: true });

    return {
      backgroundStyle,
      isBackgroundLoading,
      descriptionHtml,
      availabilityModalOpen,
      openAvailabilityModal,
      closeAvailabilityModal,
      openBookingModalFromAvailability,
      onApartmentChange
    };
  }
};
</script>

<style scoped lang="scss">
.apartment-mobile-photo {
  display: none;
}

@media (max-width: 767px) {
  .apartment-mobile-photo {
    display: block;
    width: 100%;
    height: clamp(210px, 52vw, 300px);
    background-size: cover !important;
    background-position: center center !important;
    background-attachment: scroll !important;
    background-repeat: no-repeat !important;
  }

  .apartment-mobile-photo--loading {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    color: #666;
  }
}

.apartment-modal__switcher {
  display: flex;
  justify-content: center;
  margin: 0 0 1rem;
}

.apartment-actions {
  :deep(.booking-cta) {
    width: 100%;
  }
}

@media (max-width: 767px) {
  .h-line.apartment-actions {
    text-align: stretch;
  }
}

.apartment-description :deep(p) {
  margin: 0 0 1em;
}

.apartment-description :deep(p:last-child) {
  margin-bottom: 0;
}

.apartment-description :deep(strong) {
  display: block;
  margin-bottom: 0.75em;
  font-weight: 700;
  font-synthesis: weight;
}

.furnishing-table {
  width: 100%;
  border-spacing: 0 1em;
  border-collapse: separate;
  .icon {
    vertical-align: top;
    width: 10%;
    padding-right: 0.75rem;
  }
  .description {
    width: 90%;
  }
}

@media (max-width: 767px) {
  .furnishing-table {
    border-spacing: 0 0.85em;

    .icon {
      width: auto;
      min-width: 2.75rem;
      padding-right: 1.125rem;
    }

    .description {
      width: auto;
    }
  }
}
.h-line {
  text-align: right;
  border-top: solid 1px #dfdfdf;
  padding-top: 10px;
}
</style>
